import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config';
import TestRateLimit from '../components/TestRateLimit';

const plans = [
  {
    id: 'basic',
    name: 'Basic',
    price: '4.99',
    priceId: process.env.REACT_APP_STRIPE_PRICE_BASIC
  },
  {
    id: 'pro',
    name: 'Professional',
    price: '9.99',
    priceId: process.env.REACT_APP_STRIPE_PRICE_PRO
  }
];

function Profile() {
  const { token, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();

  // Define fetch functions outside useEffect but inside component
  const fetchSubscriptionDetails = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/subscription`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSubscription(response.data.subscription);
    } catch (err) {
      if (err.response?.status === 404) {
        // If no subscription found, set to null but don't show error
        setSubscription(null);
      } else {
        setError('Failed to load subscription details');
        console.error(err);
      }
    }
  }, [token]);

  const fetchUsageStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/generate/usage`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log('Received usage stats:', response.data);
      setUsage(response.data);
    } catch (err) {
      console.error('Failed to fetch usage stats:', err);
    }
  }, [token]);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/user/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setProfile(response.data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  }, [token]);

  // Function to fetch all data
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSubscriptionDetails(),
        fetchUsageStats(),
        fetchProfile()
      ]);
    } finally {
      setLoading(false);
    }
  }, [fetchSubscriptionDetails, fetchUsageStats, fetchProfile]);

  useEffect(() => {
    fetchAllData();

    // Add event listener for when the window regains focus
    const handleFocus = () => {
      fetchAllData();
    };

    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchAllData]);

  // Add event listener for custom event from ImageGenerator
  useEffect(() => {
    const handleImageGenerated = () => {
      console.log('Image generated, refreshing usage stats');
      fetchUsageStats();
    };

    window.addEventListener('imageGenerated', handleImageGenerated);
    return () => window.removeEventListener('imageGenerated', handleImageGenerated);
  }, [fetchUsageStats]);

  const handleSubscriptionAction = async () => {
    if (subscription.status === 'cancelled') {
      navigate('/pricing');
    } else {
      try {
        setLoading(true);
        const response = await axios.post(
          `${API_URL}/create-portal-session`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        window.location.href = response.data.url;
      } catch (err) {
        setError('Failed to open subscription portal');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const getPlanName = (subscription) => {
    if (!subscription) return 'None';
    if (subscription.status === 'canceled') return 'Free';
    return plans.find(p => p.priceId === subscription.stripePriceId)?.name || 'Unknown';
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ color: '#00ffea' }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Profile
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Account Information
          </Typography>
          <Typography>
            Email: {profile?.email || user?.email || 'Loading...'}
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Subscription Details
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {subscription ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ bgcolor: 'background.paper' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {subscription.status === 'active' 
                        ? 'Active Subscription' 
                        : subscription.status === 'cancelled'
                          ? 'Cancelled Subscription'
                          : 'Subscription Status'
                      }
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Status: <strong>{subscription.status}</strong>
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Plan: <strong>{subscription.status === 'cancelled' ? 'Free' : (plans.find(p => p.priceId === subscription.plan.id)?.name || 'Unknown')}</strong>
                    </Typography>
                    {subscription.currentPeriodEnd && (
                      <Typography variant="body1" gutterBottom>
                        {subscription.status === 'cancelled' 
                          ? 'Your paid subscription has ended. Click "Select New Plan" to subscribe again.'
                          : `Current Period Ends: ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                        }
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      onClick={handleSubscriptionAction}
                      sx={{
                        mt: 2,
                        bgcolor: '#00ffea',
                        color: '#000',
                        '&:hover': {
                          bgcolor: '#00b8ff'
                        }
                      }}
                    >
                      {subscription.status === 'cancelled' 
                        ? 'Select New Plan' 
                        : 'Manage Subscription'
                      }
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body1" gutterBottom>
                No active subscription
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/pricing')}
                sx={{
                  mt: 2,
                  bgcolor: '#00ffea',
                  color: '#000',
                  '&:hover': {
                    bgcolor: '#00b8ff'
                  }
                }}
              >
                View Plans
              </Button>
            </Box>
          )}
        </Box>

        {usage && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Analyses Generated: {usage.used} / {usage.limit}
            </Typography>
            <Typography variant="body1" gutterBottom>
              Remaining: {usage.remaining}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {console.log('Reset date to display:', usage.nextReset)}
              Resets on: {new Date(usage.nextReset).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </Box>
        )}

        {process.env.NODE_ENV === 'development' && <TestRateLimit />}
      </Paper>
    </Container>
  );
}

export default Profile; 